import { apiService } from './api';
import { Area } from './areaService';

export interface ConformidadeAction {
    acao_id: string;
    titulo: string;
    descricao: string;
    responsible: string;
    responsible_id?: string;
    status: string;
    score: number;
    year: number;
    question: string;
    area: string;
    item: string;
}

export interface ConformidadeResponse {
    actions: ConformidadeAction[];
}

export interface ConformidadeFilters {
    ano?: number;
    areaId?: string;
    status?: string;
    responsavelId?: string;
    item?: string;
    titulo?: string;
    questao?: string;
}

export interface ConformidadePageResponse {
    content: ConformidadeAction[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
    first: boolean;
    last: boolean;
    numberOfElements: number;
}

export interface Entity {
    entidade_id: string;
    nome: string;
    descricao: string;
    status: string;
    codIbge?: number;
    areas: Area[];
}

export const entityService = {
    // Get all entities for the current user
    async getUserEntities(): Promise<Entity[]> {
        try {
            const response = await apiService.get<Entity[]>('/entidade/usuario');
            return response;
        } catch (error) {
            console.error('Error fetching user entities:', error);
            throw error;
        }
    },

    // Get entities for a specific user by their ID
    async getUserEntitiesById(userId: string): Promise<Entity[]> {
        try {
            const response = await apiService.get<Entity[]>(`/entidade/usuario/${userId}`);
            return response;
        } catch (error) {
            console.error('Error fetching user entities by ID:', error);
            throw error;
        }
    },

    // Get conformidade data for a specific entity with pagination and filters
    async getConformidadeData(
        entidadeId: string,
        page: number = 0,
        size: number = 10,
        filters?: ConformidadeFilters
    ): Promise<ConformidadePageResponse> {
        try {
            // Build query parameters
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('size', size.toString());

            // Add filters if provided
            if (filters?.ano) params.append('ano', filters.ano.toString());
            if (filters?.areaId) params.append('areaId', filters.areaId);
            if (filters?.status) params.append('status', filters.status);
            if (filters?.responsavelId) params.append('responsavelId', filters.responsavelId);
            if (filters?.item) params.append('item', filters.item);
            if (filters?.titulo) params.append('titulo', filters.titulo);
            if (filters?.questao) params.append('questao', filters.questao);

            const queryString = params.toString();
            const url = `/entidade/conformidade/${entidadeId}?${queryString}`;
            const response = await apiService.get<ConformidadePageResponse>(url);
            return response;
        } catch (error) {
            console.error('Error fetching conformidade data:', error);
            throw error;
        }
    },

    // Get paginated entities with filters
    async getEntities(filters?: { nome?: string; descricao?: string; status?: string }, page?: number, size?: number): Promise<any> {
        try {
            // Build query parameters
            const params = new URLSearchParams();
            if (filters?.nome) params.append('nome', filters.nome);
            if (filters?.descricao) params.append('descricao', filters.descricao);
            if (filters?.status) params.append('status', filters.status);

            // Add pagination parameters
            const currentPage = page !== undefined ? page : 0;
            const currentSize = size !== undefined ? size : 10;
            params.append('page', currentPage.toString());
            params.append('size', currentSize.toString());

            const queryString = params.toString();
            const url = `/entidade?${queryString}`;

            const response = await apiService.get(url);
            return response;
        } catch (error) {
            console.error('Error fetching entities:', error);
            throw error;
        }
    },

    // Set the current entity for the user
    async setCurrentEntity(entityId: string): Promise<void> {
        try {
            // In a real implementation, this would make an API call to set the current entity
            localStorage.setItem('currentEntityId', entityId);
        } catch (error) {
            console.error('Error setting current entity:', error);
            throw error;
        }
    },

    // Get the current entity for the user
    getCurrentEntity(): string | null {
        const entityId = localStorage.getItem('currentEntityId');
        return entityId;
    },

    // Create a new entity
    async createEntity(entityData: { nome: string; descricao?: string; status?: string; codIbge?: number }): Promise<Entity> {
        try {
            const response = await apiService.post<Entity>('/entidade', entityData);
            return response;
        } catch (error) {
            console.error('Error creating entity:', error);
            throw error;
        }
    },

    // Update an existing entity
    async updateEntity(entityId: string, entityData: { nome: string; descricao?: string; status?: string; codIbge?: number }): Promise<Entity> {
        try {
            const response = await apiService.put<Entity>(`/entidade/${entityId}`, entityData);
            return response;
        } catch (error) {
            console.error('Error updating entity:', error);
            throw error;
        }
    },

}; 